import { useState } from 'react';

interface ReturnModalProps {
  onClose: () => void;
}

export default function ReturnModal({ onClose }: ReturnModalProps) {
  const [returnType, setReturnType] = useState<'with-receipt' | 'without-receipt' | null>(null);
  const [receiptId, setReceiptId] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const returnItems = [
    { id: 1, name: 'Organic Whole Milk 1L', qty: 2, price: 4.99, total: 9.98 },
    { id: 2, name: 'Fresh Bread Whole Wheat', qty: 1, price: 3.49, total: 3.49 },
    { id: 3, name: 'Premium Coffee Beans 500g', qty: 1, price: 12.99, total: 12.99 },
  ];

  const returnReasons = [
    'Defective/Damaged',
    'Wrong Item',
    'Changed Mind',
    'Expired Product',
    'Quality Issue',
    'Other',
  ];

  const toggleItem = (id: number) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="bg-cyan-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Process Return</h2>
            <p className="text-sm text-cyan-100 whitespace-nowrap">Select items and reason for return</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-cyan-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!returnType ? (
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4 whitespace-nowrap">Select Return Type</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setReturnType('with-receipt')}
                  className="p-6 border-2 border-gray-200 hover:border-cyan-500 rounded-xl transition-all cursor-pointer group"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-cyan-100 group-hover:bg-cyan-500 text-cyan-600 group-hover:text-white rounded-full mx-auto mb-4 transition-colors">
                    <i className="ri-file-list-3-line text-3xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 whitespace-nowrap">With Receipt</h4>
                  <p className="text-sm text-gray-600 whitespace-nowrap">Customer has original receipt or transaction ID</p>
                </button>

                <button
                  onClick={() => setReturnType('without-receipt')}
                  className="p-6 border-2 border-gray-200 hover:border-amber-500 rounded-xl transition-all cursor-pointer group"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-100 group-hover:bg-amber-500 text-amber-600 group-hover:text-white rounded-full mx-auto mb-4 transition-colors">
                    <i className="ri-file-forbid-line text-3xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 whitespace-nowrap">Without Receipt</h4>
                  <p className="text-sm text-gray-600 whitespace-nowrap">Requires supervisor approval</p>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {returnType === 'with-receipt' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Receipt Lookup</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={receiptId}
                      onChange={(e) => setReceiptId(e.target.value)}
                      placeholder="Scan receipt barcode or enter transaction ID"
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-cyan-500"
                    />
                    <button className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer">
                      <i className="ri-search-line mr-1"></i>
                      Lookup
                    </button>
                  </div>
                </div>
              )}

              {returnType === 'without-receipt' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <i className="ri-alert-line text-amber-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1 whitespace-nowrap">Supervisor Approval Required</p>
                    <p className="text-sm text-amber-800 whitespace-nowrap">Returns without receipt require manager authorization and may have different refund policies.</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 whitespace-nowrap">Select Items to Return</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {returnItems.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      className={`p-4 border-b border-gray-200 last:border-b-0 cursor-pointer transition-colors ${
                        selectedItems.includes(item.id) ? 'bg-cyan-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-colors ${
                          selectedItems.includes(item.id)
                            ? 'bg-cyan-600 border-cyan-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedItems.includes(item.id) && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{item.name}</p>
                          <p className="text-xs text-gray-600 whitespace-nowrap">Qty: {item.qty} × {item.price.toFixed(2)}</p>
                        </div>
                        <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{item.total.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedItems.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Return Reason (Required)</label>
                  <select className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-cyan-500">
                    <option value="">Select reason...</option>
                    {returnReasons.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedItems.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Additional Notes (Optional)</label>
                  <textarea
                    rows={3}
                    placeholder="Enter any additional details about the return..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-cyan-500 resize-none"
                  ></textarea>
                </div>
              )}

              {selectedItems.length > 0 && (
                <div className="p-4 bg-gray-900 text-white rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm whitespace-nowrap">Items Selected:</span>
                    <span className="text-sm font-semibold whitespace-nowrap">{selectedItems.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold whitespace-nowrap">Refund Amount:</span>
                    <span className="text-2xl font-bold whitespace-nowrap">
                      {returnItems
                        .filter(item => selectedItems.includes(item.id))
                        .reduce((sum, item) => sum + item.total, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={() => returnType ? setReturnType(null) : onClose()}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            {returnType ? 'Back' : 'Cancel'}
          </button>
          
          {returnType && (
            <button
              disabled={selectedItems.length === 0}
              className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                selectedItems.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white cursor-pointer'
              }`}
            >
              <i className="ri-arrow-go-back-line mr-2"></i>
              Process Return
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

